import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { minsToHhMm } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyVidsUserDetail } from './companyVidsApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID_NO_X,
  toneChip,
  paletteAt
} from '../../../../component/common/designSystem';
import { kindChipClass, statusChipClass } from './vidsTheme';

/**
 * Shoorah Videos → User profile.
 * One person's viewing across both libraries: totals, the videos they gave the
 * most time to, and their most recent watches. `fetcher`/`backPath`/`backLabel`
 * are props so the super-admin Vids Hub can reuse the page against the unscoped
 * endpoint.
 */
const VidsUserDetail = ({ fetcher = fetchCompanyVidsUserDetail, backPath, backLabel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const topVideos = detail?.topVideos || [];
  const history = detail?.watchHistory || [];

  // Ranked by minutes rather than by count: for video, time given is the
  // clearer signal of what someone actually engaged with.
  const topOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topVideos.map((entry, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topVideos.map((entry) => entry.video) },
    tooltip: { y: { formatter: (val) => minsToHhMm(val) } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.plays}
          title="Videos Watched"
          value={user ? user.videosWatched : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Completed"
          value={user ? `${user.completions} (${user.completionRate}%)` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Watch Time"
          value={user ? minsToHhMm(user.totalWatchTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Avg per Video"
          value={user ? `${user.avgWatchMins} min` : '-'}
        />
        <StatCard icon={STAT_ICONS.rating} title="Likes" value={user ? user.likes : '-'} />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Top Video"
          value={detail?.mostWatchedVideo || '-'}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Top Videos" subtitle="Where this user spent the most time">
          {topVideos.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[{ name: 'Watch time', data: topVideos.map((entry) => entry.watchMins) }]}
              options={topOptions}
            />
          ) : (
            <PanelEmpty>No video activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Watch History" subtitle="Most recently watched videos">
          {history.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Last watched</th>
                  <th className="py-2 pr-4 font-medium">Video</th>
                  <th className="py-2 pr-4 font-medium">Library</th>
                  <th className="py-2 pr-4 font-medium">Progress</th>
                  <th className="py-2 font-medium text-right">Watched</th>
                </>
              }
            >
              {history.map((entry, index) => (
                <tr key={`${entry.date}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{entry.date || '-'}</td>
                  <td className="py-2 pr-4">{entry.video}</td>
                  <td className="py-2 pr-4">
                    <Chip className={kindChipClass(entry.kind)}>{entry.kind}</Chip>
                  </td>
                  <td className="py-2 pr-4">
                    {entry.status ? (
                      <Chip className={statusChipClass(entry.status)}>{entry.status}</Chip>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 text-right">{minsToHhMm(entry.watchMins)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No watch history yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

VidsUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string.isRequired,
  backLabel: PropTypes.string
};

VidsUserDetail.defaultProps = {
  backLabel: 'Back'
};

export default VidsUserDetail;
